import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, MapPin, Clock, Zap } from 'lucide-react';
import { fadeUp } from './Shared';

const HERO_BG = 'https://customer-assets.emergentagent.com/job_ace-padel/artifacts/iuitvfvu_Space%20%23%20image.jpg';
const HERO_IMG = 'https://customer-assets.emergentagent.com/job_ace-padel/artifacts/93ujk2kh_image%20with%20customers.jpg';

const STATS = [
  { icon: MapPin, value: '2 Venues Open', sub: 'Kochi · Kakkanad' },
  { icon: Zap, value: '20 Courts', sub: 'Across 5 cities by 2027' },
  { icon: Clock, value: '6 AM – Midnight', sub: 'Open every day' },
];

export const NlHero = ({ onBook }) => (
  <section id="top" data-testid="nl-hero" className="relative min-h-[100svh] flex items-end lg:items-center overflow-hidden">
    <img src={HERO_BG} alt="" className="absolute inset-0 w-full h-full object-cover opacity-30 scale-105" />
    <div className="absolute inset-0 bg-gradient-to-b from-[#0B0D11]/70 via-[#0B0D11]/60 to-[#0B0D11]" />
    <div className="absolute inset-0 bg-gradient-to-r from-[#0B0D11] via-[#0B0D11]/40 to-transparent" />
    <div className="nl-glow w-[36rem] h-[36rem] bg-amber-500/20 -top-40 -right-40" />

    <div className="nl-container relative w-full pt-24 pb-12 lg:pt-28 lg:pb-16 grid lg:grid-cols-12 gap-12 lg:gap-16 items-center">
      <motion.div initial="hidden" animate="show" className="lg:col-span-7">
        <motion.span variants={fadeUp} custom={0} className="nl-label">Kerala's Premier Padel &amp; Pickleball Club</motion.span>
        <motion.h1
          variants={fadeUp}
          custom={1}
          className="mt-4 text-4xl sm:text-5xl lg:text-6xl xl:text-[4.5rem] font-black uppercase leading-[0.92] tracking-tight"
        >
          Hash your
          <br />
          game<span className="text-amber-500">.</span>
          <br />
          <span className="nl-outline-text">at space#</span>
        </motion.h1>
        <motion.p variants={fadeUp} custom={2} className="nl-lead mt-5">
          The world's fastest-growing sport, played on world-class courts. Book a slot, join a clinic, or
          just show up and find a game — everyone's welcome at space#.
        </motion.p>
        <motion.div variants={fadeUp} custom={3} className="mt-7 flex flex-wrap items-center gap-4">
          <button onClick={onBook} data-testid="nl-hero-book-btn" className="nl-btn nl-btn-primary">
            Book a Court Now <ArrowRight className="w-4 h-4" />
          </button>
          <a href="#locations" data-testid="nl-hero-locations-btn" className="nl-btn nl-btn-ghost">
            Explore Locations
          </a>
        </motion.div>
        <motion.div variants={fadeUp} custom={4} className="mt-6 flex items-center gap-3 flex-wrap">
          <span className="text-xs uppercase tracking-[0.18em] text-slate-500 font-semibold mr-1">Book on the app</span>
          <a href="https://play.google.com/store/apps/details?id=com.padelplay.piplay&hl=en_IN" target="_blank" rel="noopener noreferrer" data-testid="nl-hero-google-play">
            <img src="https://upload.wikimedia.org/wikipedia/commons/7/78/Google_Play_Store_badge_EN.svg" alt="Get it on Google Play" className="h-10 opacity-80 hover:opacity-100 transition-opacity" />
          </a>
          <a href="https://apps.apple.com/in/app/pi-play-play-padel-pickleball/id6451375568" target="_blank" rel="noopener noreferrer" data-testid="nl-hero-app-store">
            <img src="https://upload.wikimedia.org/wikipedia/commons/3/3c/Download_on_the_App_Store_Badge.svg" alt="Download on the App Store" className="h-10 opacity-80 hover:opacity-100 transition-opacity" />
          </a>
        </motion.div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, x: 40 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.9, delay: 0.3, ease: [0.2, 0.8, 0.2, 1] }}
        className="lg:col-span-5 relative"
      >
        <div className="relative rounded-[1.5rem] overflow-hidden border border-white/10 aspect-[4/5] lg:aspect-auto lg:h-[min(64vh,36rem)] shadow-[0_40px_80px_-30px_rgba(0,0,0,0.9)]">
          <img src={HERO_IMG} alt="space# community on court" className="nl-img-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0B0D11] via-transparent to-[#0B0D11]/40" />
          <div className="absolute top-5 left-5 nl-glass border border-amber-500/30 rounded-full pl-2 pr-4 py-1.5 flex items-center gap-2.5">
            <span className="nl-status-dot bg-emerald-400 w-2 h-2 rounded-full animate-pulse" />
            <span className="text-xs font-bold tracking-wide"><span className="text-amber-500 uppercase text-[0.65rem] tracking-[0.18em] mr-2">Now open</span>Kakkanad · 4 Courts</span>
          </div>
          <div className="absolute bottom-5 left-5 right-5 grid grid-cols-3 gap-2">
            {STATS.map(({ icon: Icon, value, sub }) => (
              <div key={value} className="nl-glass rounded-xl border border-white/10 p-3">
                <Icon className="w-4 h-4 text-amber-500 mb-2" />
                <div className="nl-display font-bold text-sm leading-tight">{value}</div>
                <div className="text-[0.65rem] text-slate-400 mt-0.5 leading-tight">{sub}</div>
              </div>
            ))}
          </div>
        </div>
      </motion.div>
    </div>
  </section>
);

const WORDS = ['Padel', 'Pickleball', 'Coaching', 'Tournaments', 'Community', 'Pro Shop', 'Kochi', 'Kakkanad'];

export const NlMarquee = () => (
  <div className="nl-marquee py-5" aria-hidden="true">
    <div className="nl-marquee-track">
      {[...WORDS, ...WORDS].map((w, i) => (
        <span key={i} className="nl-display text-lg md:text-2xl font-extrabold uppercase tracking-tight px-6 flex items-center gap-6 text-slate-300">
          {w} <span className="text-amber-500 text-base">#</span>
        </span>
      ))}
    </div>
  </div>
);
